import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { api, unwrap } from '../api/client';
import { usePositions } from '../api/hooks';
import { ErrorNote } from './ui';

interface Props {
  agentId: string;
  signedDecrees: Array<{ id: string; number: string | null; subject: string }>;
}

/** Affecter un agent à un poste : poste vacant ou non, date de début, arrêté signé facultatif (reprise). */
export function AssignDialog({ agentId, signedDecrees }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const positions = usePositions();
  const qc = useQueryClient();
  const [form, setForm] = useState({ positionId: '', startsOn: new Date().toISOString().slice(0, 10), quotity: '100', decreeId: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const assign = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/assignments', {
          body: { agentId, positionId: form.positionId, startsOn: form.startsOn, quotity: Number(form.quotity), decreeId: form.decreeId || null },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      void qc.invalidateQueries({ queryKey: ['positions'] });
      dialog.current?.close();
    },
  });

  return (
    <>
      <button className="btn btn-sm" onClick={() => dialog.current?.showModal()}>
        Affecter à un poste
      </button>
      <dialog ref={dialog} aria-labelledby="assign-title">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            assign.mutate();
          }}
        >
          <h2 id="assign-title">Nouvelle affectation</h2>
          <p className="muted small">L'affectation en cours, s'il y en a une, sera clôturée la veille de la date de début.</p>
          <div className="field">
            <label htmlFor="as-position">Poste</label>
            <select id="as-position" required value={form.positionId} onChange={set('positionId')}>
              <option value="">— Choisir —</option>
              {(positions.data ?? []).map((p) => (
                <option key={p.position.id} value={p.position.id}>
                  {p.position.code} · {p.position.title} ({p.service.code}){p.occupied ? ' · occupé' : ' · vacant'}
                </option>
              ))}
            </select>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="as-start">À partir du</label>
              <input id="as-start" type="date" required value={form.startsOn} onChange={set('startsOn')} />
            </div>
            <div className="field">
              <label htmlFor="as-quotity">Quotité (%)</label>
              <input id="as-quotity" type="number" min={1} max={100} required value={form.quotity} onChange={set('quotity')} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="as-decree">Arrêté fondant l'affectation</label>
            <select id="as-decree" value={form.decreeId} onChange={set('decreeId')}>
              <option value="">Aucun (reprise de l'existant, tracé comme tel)</option>
              {signedDecrees.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.number} · {d.subject}
                </option>
              ))}
            </select>
          </div>
          <ErrorNote error={assign.error} />
          <div className="actions">
            <button type="submit" className="btn btn-primary" disabled={assign.isPending}>
              Affecter
            </button>
            <button type="button" className="btn" onClick={() => dialog.current?.close()}>
              Annuler
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
